import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead } from "@/components/shared";

const HOME_ROWS = [
  ["Studio", "$50", "$90"],
  ["1 Bedroom", "$60", "$110"],
  ["2 Bedroom", "$80", "$140"],
  ["3 Bedroom", "$100", "$175"],
  ["4 Bedroom", "$125", "$210"],
  ["5+ Bedroom", "From $150", "Quote required"],
];

const TURNOVER_ROWS = [
  ["Studio", "$60", "—"],
  ["1 Bedroom", "$70", "$65"],
  ["2 Bedroom", "$90", "$85"],
  ["3 Bedroom", "$115", "$105"],
  ["4 Bedroom", "$140", "$130"],
  ["Each additional bedroom", "+$25", "—"],
];

const ADD_ONS = [
  ["Fresh linen change — clean linen already on-site", "+$10 per bed"],
  ["Wash, dry & fold linen", "+$25 per load"],
  ["Extra bathroom", "+$15"],
  ["Inside refrigerator", "+$25"],
  ["Inside oven", "+$30"],
  ["Interior windows", "From $25"],
  ["Balcony / patio refresh", "+$20"],
  ["Same-day / rush booking", "+$20"],
  ["Pet hair / excessive sand", "From +$20"],
  ["Excessive cleaning", "+$32 per additional cleaner-hour"],
  ["Guest welcome setup", "+$15"],
  ["Grocery / welcome basket setup", "+$20 + cost of items"],
];

const PriceTable = ({ title, copy, head2, head3, rows, footnote, testid }) => (
  <div className="border border-navy/10 bg-white" data-testid={testid}>
    <div className="border-b border-navy/10 p-5 sm:p-7">
      <h3 className="font-serif text-2xl text-navy">{title}</h3>
      <p className="mt-2 text-[13px] leading-relaxed text-navy/55">{copy}</p>
    </div>
    <div>
      <div className="grid grid-cols-[1fr_auto_auto] items-baseline gap-2 border-b border-navy/10 bg-sand-light px-4 py-3 text-[9px] font-semibold uppercase tracking-[0.12em] text-navy/50 sm:gap-4 sm:px-7 sm:text-[10px]">
        <span>Property</span><span className="w-16 text-right sm:w-24">{head2}</span><span className="w-16 text-right sm:w-24">{head3}</span>
      </div>
      {rows.map(([name, a, b], i) => (
        <div key={name} className={`grid grid-cols-[1fr_auto_auto] items-baseline gap-2 px-4 py-3.5 text-[13px] sm:gap-4 sm:px-7 sm:py-4 sm:text-sm ${i % 2 ? "bg-sand-light/50" : ""}`}>
          <span className="text-navy/80">{name}</span>
          <span className="w-16 text-right font-mono text-navy sm:w-24">{a}</span>
          <span className="w-16 text-right font-mono text-bermuda-dark sm:w-24">{b}</span>
        </div>
      ))}
      {footnote && <p className="px-4 py-4 text-xs leading-relaxed text-navy/50 sm:px-7">{footnote}</p>}
    </div>
  </div>
);

export default function CleaningPricing() {
  return (
    <section id="rates" className="scroll-mt-24 bg-sand-light" data-testid="cleaning-pricing">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-28">
        <SectionHead
          overline="Transparent Flat-Rate Pricing"
          title={<>Affordable Cleaning. <span className="italic text-bermuda-dark">Hotel-Level Results.</span></>}
          copy="Prices from $50. No complicated quotes. No surprise hourly bill — the number we quote is the number you pay, every time."
        />

        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          <Reveal>
            <PriceTable
              testid="home-cleaning-table"
              title="Home Cleaning"
              copy="Regular cleans and deep cleans for island homes — bathrooms, kitchen, beds, floors, mirrors, dusting and tidying. Deep cleans add baseboards, doors, cabinet fronts, window sills, fans and detailed buildup removal."
              head2="Regular"
              head3="Deep Clean"
              rows={HOME_ROWS}
            />
          </Reveal>
          <Reveal delay={0.1}>
            <PriceTable
              testid="turnover-table"
              title="Airbnb Turnover Cleaning"
              copy="Guest-to-guest turnovers carry more responsibility — staging, beds, towels, inventory checks and a property-ready finish before check-in. Priced accordingly, still affordable."
              head2="Turnover"
              head3="Host Partner"
              rows={TURNOVER_ROWS}
              footnote="Host Partner Rate: for owners who guarantee regular turnovers — recurring work, reliably priced. Same-day turnovers never carry a rush fee for partner hosts; reliability is part of why hosts choose us."
            />
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14">
            <h3 className="font-serif text-2xl text-navy">Optional add-ons</h3>
            <p className="mt-2 text-sm text-navy/55">Base prices stay low because labour-intensive extras are optional — add only what a visit actually needs.</p>
            <div className="mt-7 grid gap-px bg-navy/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="cleaning-addons">
              {ADD_ONS.map(([name, price], i) => (
                <div key={name} className="flex items-center justify-between gap-4 bg-white px-6 py-4">
                  <span className="text-[13px] text-navy/75">{name}</span>
                  <span className="whitespace-nowrap font-mono text-xs text-bermuda-dark">{price}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-10 border-l-2 border-bermuda bg-white p-6 text-xs leading-relaxed text-navy/55" data-testid="cleaning-condition-note">
            Prices are based on properties in average maintained condition. Excessive dirt, excessive trash, parties,
            pet waste, heavy buildup or properties requiring additional cleaning time may incur an additional charge.
          </p>
        </Reveal>

        <Reveal delay={0.25}>
          <button
            data-testid="cleaning-pricing-cta"
            onClick={() => document.getElementById("cleaning-quote")?.scrollIntoView({ behavior: "smooth" })}
            className="group mt-10 inline-flex items-center gap-2.5 bg-navy px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-bermuda-dark"
          >
            Get My Exact Quote
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
